import { IStickConfig, IInputConfig, IAssetsConfig } from "../types/game-config-type";
import { Keyboard } from "../input/keyboard";
import { Mouse } from "../input/mouse";
import { GameConfig } from "../config/game-config";
import { Assets } from "./resources";
import { table } from "./table";
import { Vector2 } from "../math/vector2";
import { mapRange } from "../math/helper";
import gameApplication from "./game-app";

const inputConfig: IInputConfig = GameConfig.input;
const stickConfig: IStickConfig = GameConfig.stick;
const sprites: IAssetsConfig = GameConfig.sprites;
const sounds: IAssetsConfig = GameConfig.sounds;

export class CueStick {
  private sprite: HTMLImageElement = Assets.getSprite(sprites.paths.stick);
  private ballRotation = 0;
  private origin: Vector2 = Vector2.copy(stickConfig.origin);
  private hitPower: number = 0;
  
  private isVisible: boolean = true;
  private isMovable: boolean = true; // this is disabled when it's the opponent turn
  private isEnabled: boolean = true; // this is disabled when the mouse is over the hit position
  private isActive: boolean = true; // this is disabled when a modal is open and the game is in progress

  private cueHitPosition: Vector2 = Vector2.zero; // cue ball hit position
  private cueHitOrigin: number = 0; // cue hit origin position

  // flag that indicates, the player touched the cue ball while aiming
  private hasShot: boolean = false;

  // flag that indicates, the cue was drawn back while aiming
  private hasDrawn: boolean = false;

  // flag that indicates whether to show hitline while aiming
  private showHitLine: boolean = true;

  // utility variables to calculate mouse speed
  private prevOffset: Vector2 = Vector2.zero;
  private prevTime: number = 0;
  private prevMousePosition: Vector2 = Vector2.zero;

  public get position(): Vector2 {
    return Vector2.copy(this.cuePosition);
  }

  public get rotation(): number {
    return this.ballRotation;
  }
  public set rotation(value: number) {
    this.ballRotation = value;
  }

  public get power(): number {
    return this.hitPower;
  }

  public set power(value: number) {
    this.hitPower = value;
  }

  public get shot(): boolean {
    return this.hasShot;
  }

  public set shot(value: boolean) {
    this.hasShot = value;
  }

  public get visible(): boolean {
    return this.isVisible;
  }
  public set visible(value: boolean) {
    this.isVisible = value;
  }

  public set movable(value: boolean) {
    this.isMovable = value;
  }

  public get hitPosition(): Vector2 {
    return this.cueHitPosition;
  }

  public set hitPosition(value: Vector2) {
    this.cueHitPosition = value;
  }

  public get hitOrigin(): number {
    return this.cueHitOrigin;
  }

  public set hitOrigin(value: number) {
    this.cueHitOrigin = value;
  }

  public get enabled(): boolean {
    return this.isEnabled;
  }

  public set enabled(value: boolean) {
    this.isEnabled = value;
  }

  public get active(): boolean {
    return this.isActive;
  }

  public set active(value: boolean) {
    this.isActive = value;
  }

  constructor(private cuePosition: Vector2) { }
  
  public setHitPosition(position: Vector2): void {
    this.cueHitPosition = position;
  }

  public setMoveable(isOpponentTurn: boolean): void {
    if (isOpponentTurn) {
      // turn off cue stick action and listen for the opponent cue stick
      this.movable = false;
    } else {
      // allow the player to move the cue stick to shoot
      this.movable = true;
    }
  }

  public update(): void {
    if (this.isMovable) {
      this.updateRotation();
      this.updateHitPosition();
      this.updatePower();
    }
  }

  public draw(): void {
    if (this.isVisible) {
      table.drawImage(this.sprite, this.cuePosition,
        this.ballRotation, this.origin.addY(this.cueHitOrigin));
      if (this.isMovable) {
        if (this.showHitLine) {
          table.drawHitLine(this.cuePosition, this.ballRotation);
        }
      }
    }
  }

  public hide(): void {
    this.isVisible = false;
    this.isMovable = false;
    this.resetCue();
  }

  public show(position: Vector2): void {
    this.hitPower = 0;
    this.cuePosition = position;
    this.origin = Vector2.copy(stickConfig.origin);
    this.isVisible = true;
  }

  public shoot(): void {
    this.origin = Vector2.copy(stickConfig.shotOrigin);
    const volume: number = mapRange(this.hitPower, 0, stickConfig.maxPower, 0, 1);
    Assets.playSound(sounds.paths.CUE_HIT, volume);
  }

  /**
   * Update the cue position. 
   * This updates the cue position when the cue is pulled back to hit the cue ball. 
   */
  private updateOffset(): number {
    const offset = Mouse.position.subtract(Mouse.pressedPosition);
    let originOffset = -(offset.x * Math.cos(this.ballRotation) + offset.y * Math.sin(this.ballRotation));
    
    if (originOffset > 3) {
      this.hasDrawn = true;
    }
    if (originOffset > stickConfig.maxPower) {
      originOffset = stickConfig.maxPower;
    }
  
    if (originOffset < 0) {
      if (this.hasDrawn) {
        if (originOffset < -5) {
          originOffset = -5;
          if (this.hasDrawn) { 
            this.hasShot = false;
            this.hasDrawn = false;
          }
        }
      } else {
        originOffset = 0;
      }
    }

    this.origin = Vector2.copy(stickConfig.origin).addX(originOffset * 4);
    return originOffset;
  }

  /**
   * Update the cue speed.
   * This is determined by the speed of the cue stick when striking the cue ball.
   * NOTE: This is currently not used.
   */
  private updateSpeed(): void {
    const offset = Mouse.position.subtract(Mouse.pressedPosition);
    
    if (this.prevTime !== 0 && this.prevOffset.length > 0) {
      const currentTime = Date.now();
      const movement = offset.subtract(this.prevOffset);
      const movementX = movement.x * Math.cos(this.ballRotation) + movement.y * Math.sin(this.ballRotation);

      if (movementX <= 0) {
        this.hitPower = 0;
      } else {
        this.hitPower = (movementX / (currentTime - this.prevTime)) * 200;
        if (this.hitPower > stickConfig.maxPower) {
          this.hitPower = stickConfig.maxPower;
        }
      }
    }

    this.prevOffset = offset;
  }

  /**
   * Stop the cue movement. Reset the cue speed to zero.
   */
  private resetSpeed(): void {
    this.prevOffset = Vector2.zero;
    this.prevTime = 0;
    this.origin = Vector2.copy(stickConfig.origin);
    this.hasDrawn = false;
  }

  /**
   * This is used to measure the speed the cue stick is pushed forward.
   */
  private initSpeed(): void {
    this.prevOffset = Mouse.position.subtract(Mouse.pressedPosition);
    this.prevTime = Date.now();
    this.hitPower = 0;
  }

  /**
   * Update the hit power.
   */
  private updatePower(): void {
    if (Mouse.isPressed(inputConfig.mouseShootButton)) {
      this.initSpeed();
    }
    
    if (Mouse.isDown(inputConfig.mouseShootButton)) {
      this.hitPower = this.updateOffset(); // update the cue speed when shooting
    }

    if (Mouse.isReleased(inputConfig.mouseShootButton)) {
      if (this.hasDrawn) { 
        this.hasShot = true;
        this.hasDrawn = false;
      }

      this.resetSpeed();
    }
  }

  /**
   * Adjust the cue stick power with the keys.
   * This uses the "A" and "Z" keys to adjust the cue stick power.
   * NOTE: This is currently not used. This is from the legacy code.
   */
  private updatePowerWithKeys(): void {
    if (Keyboard.isDown(inputConfig.increaseShotPowerKey) && this.isLessThanMaxPower()) {
      this.increasePower();
    }
    else if (Keyboard.isDown(inputConfig.decreaseShotPowerKey) && this.isMoreThanMinPower()) {
      this.decreasePower();
    }
  }

  /**
   * Legacy power code methods.
   * These are used in the legacy "updatePowerWithKeys" function.
   * NOTE: These are currently not used. These are from the legacy code.
   */
  private increasePower(): void {
    this.power += stickConfig.powerToAddPerFrame;
    this.origin.addToX(stickConfig.movementPerFrame);
  }

  private decreasePower(): void {
    this.power -= stickConfig.powerToAddPerFrame;
    this.origin.addToX(-stickConfig.movementPerFrame);
  }
  
  private isLessThanMaxPower(): boolean {
    return this.power < stickConfig.maxPower;
  }

  private isMoreThanMinPower(): boolean {
    return this.power >= 0;
  }

  /**
   * This enables the player to update the hit position with the arrow keys.
   */
  private updateHitPosition(): void {
    if (!Mouse.isDown(inputConfig.mouseShootButton)) {
      const { x, y } = this.cueHitPosition;
      
      if (Keyboard.isPressed(inputConfig.upHitKey)) {
        const newY = Math.abs(y + 0.1) > Math.sqrt(1 - x * x) ? Math.sqrt(1 - x * x) : y + 0.1;
        this.cueHitPosition = new Vector2(x, newY);        
      }
      if (Keyboard.isPressed(inputConfig.downHitKey)) {
        const newY = Math.abs(y - 0.1) > Math.sqrt(1 - x * x) ? -Math.sqrt(1 - x * x) : y - 0.1;
        this.cueHitPosition = new Vector2(x, newY);   
      }
      if (Keyboard.isPressed(inputConfig.rightHitKey)) {
        const newX = Math.abs(x + 0.1) > Math.sqrt(1 - y * y) ? Math.sqrt(1 - y * y) : x + 0.1;
        this.cueHitPosition = new Vector2(newX, y);
      }
      if (Keyboard.isPressed(inputConfig.leftHitKey)) {
        const newX = Math.abs(x - 0.1) > Math.sqrt(1 - y * y) ? -Math.sqrt(1 - y * y) : x - 0.1;
        this.cueHitPosition = new Vector2(newX, y);
      }
    }
  }

  /**
   * Rotate the cue stick. Update the cue stick angle.
   */
  private updateRotation(): void {
    if (!Mouse.isDown(inputConfig.mouseShootButton)) {
      if (this.prevMousePosition.subtract(Mouse.position).length > 0.01) {
        const opposite: number = Mouse.position.y - this.cuePosition.y;
        const adjacent: number = Mouse.position.x - this.cuePosition.x;
        this.ballRotation = Math.atan2(opposite, adjacent);
        this.prevMousePosition = Mouse.position;
        if (this.isVisible && Mouse.isPressed(inputConfig.mouseToggleHitlineButton)) {
          this.showHitLine = !this.showHitLine;
        }
        this.prevMousePosition = Vector2.zero;

        if (gameApplication.getGameKind() !== 'PRACTICE') {
          // send the player cue stick rotation to the opponent
          gameApplication.sendHitDirection(new Vector2(adjacent, opposite));
        }
      }
    } else {
      this.prevMousePosition = Vector2.copy(Mouse.position);
    }
  }

  /**
   * Reset the cue stick to netural.
   */
  private resetCue(): void {
    this.resetSpeed();
    this.cueHitPosition = Vector2.zero;
    this.cueHitOrigin = 0;
  }
}
